import type { Segment, SegmentClaimRegistry, SegmentReport, SegmentSourceTier } from "../types"

import { ClaimCitationChip } from "./claim-citation-chip"
import {
  BulletList,
  ConfidenceBadge,
  Divider,
  NewsFeed,
  SectionCard,
  SourceLinks,
} from "./segment-detail-primitives"

type MarketTabProps = {
  claimRegistry?: SegmentClaimRegistry | null
  report: SegmentReport | null
  segment: Segment
  onOpenClaim?: (claimId: string) => void
}

export function MarketTab({ claimRegistry, report, segment, onOpenClaim }: MarketTabProps) {
  const summary = report?.summary.executiveSummary ?? [
    `${segment.name} sits in the ${segment.marketName} market.`,
    `Forecast growth is ${segment.cagr}% CAGR over 5 years.`,
  ]
  const changes = report?.summary.keyChangesSinceLastRun ?? []
  const executiveSummaryClaim = claimRegistry?.claims.find(
    (claim) => claim.status === "approved" && claim.fieldPath === "summary.executiveSummary"
  )
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 xl:grid-cols-2">
        <SectionCard title="Executive summary">
          <ul className="text-foreground grid gap-2 text-sm">
            {summary.map((item) => (
              <li key={item} className="bg-muted/50 rounded-md p-3">
                {item}
                {executiveSummaryClaim && onOpenClaim ? (
                  <ClaimCitationChip claim={executiveSummaryClaim} onOpen={onOpenClaim} />
                ) : null}
              </li>
            ))}
          </ul>
          {changes.length > 0 && (
            <div className="mt-4">
              <h4 className="text-muted-foreground text-xs font-semibold tracking-[0.12em] uppercase">
                Key changes since last run
              </h4>
              <ul className="text-muted-foreground mt-2 grid gap-2 text-sm">
                {changes.map((change) => (
                  <li key={change}>{change}</li>
                ))}
              </ul>
            </div>
          )}
        </SectionCard>
        <SectionCard title="Applications, trends & regulation">
          <QualitativeBlock
            items={report?.marketOverview.keyApplications ?? ["Run a full report to populate."]}
            meta={report?.marketOverview.sections?.keyApplications}
            title="Key applications"
            trustByUrl={report?.grounded?.sourceTrustByUrl}
          />
          <Divider />
          <QualitativeBlock
            items={report?.marketOverview.macroTrends ?? []}
            meta={report?.marketOverview.sections?.macroTrends}
            title="Macro trends"
            trustByUrl={report?.grounded?.sourceTrustByUrl}
          />
          <Divider />
          <QualitativeBlock
            meta={report?.marketOverview.sections?.regulatoryFactors}
            title="Regulatory factors"
            trustByUrl={report?.grounded?.sourceTrustByUrl}
            items={
              report?.marketOverview.regulatoryFactors ?? [
                "Run a full report to surface regulatory factors.",
              ]
            }
          />
        </SectionCard>
      </div>
      <SectionCard title="Technology & business-model trends">
        <div className="grid gap-4 lg:grid-cols-2">
          <QualitativeBlock
            items={report?.trendAndInnovation.technologyTrends ?? []}
            title="Technology trends"
          />
          <QualitativeBlock
            items={report?.trendAndInnovation.businessModelTrends ?? []}
            title="Business model trends"
          />
        </div>
      </SectionCard>
      <SectionCard title="Market news (last 90 days)">
        <NewsFeed hasReport={report !== null} items={report?.marketOverview.newsFeed ?? []} />
      </SectionCard>
    </div>
  )
}

function QualitativeBlock({
  items,
  meta,
  title,
  trustByUrl,
}: {
  items: string[]
  meta?: { confidence?: "low" | "medium" | "high"; sources?: string[] }
  title: string
  trustByUrl?: Record<string, SegmentSourceTier>
}) {
  return (
    <div>
      <div className="mb-2 flex items-center gap-2">
        <h4 className="text-muted-foreground text-xs font-semibold tracking-[0.12em] uppercase">
          {title}
        </h4>
        {meta?.confidence ? <ConfidenceBadge confidence={meta.confidence} /> : null}
      </div>
      <BulletList items={items} />
      {meta?.sources?.length ? (
        <SourceLinks sources={meta.sources} trustByUrl={trustByUrl} />
      ) : null}
    </div>
  )
}
